/**
 * ─── DESIGN PATTERN: OBSERVER (publish/subscribe) ─────────────────────────
 *
 * Problema architetturale: lo stato dell'app (news caricate, pagina corrente,
 * ricerca, ordinamento, preferiti, errori) è letto da più moduli di UI
 * indipendenti — lista, statistiche, classifica dei domini, toolbar.
 * Senza un punto unico di verità ogni handler dovrebbe ricordarsi di
 * aggiornare a mano tutte le porzioni di pagina interessate.
 *
 * Lo Store custodisce lo stato, e chi è interessato si iscrive: a ogni
 * `setState` tutti gli osservatori vengono notificati e si ridisegnano da soli.
 * Il flusso diventa unidirezionale: azione → stato → render.
 */

/**
 * @template T
 * @param {T} initialState
 * @returns {{
 *   getState: () => T,
 *   setState: (patch: Partial<T> | ((state: T) => Partial<T>)) => T,
 *   subscribe: (listener: (state: T, prev: T) => void) => () => void
 * }}
 */
export function createStore(initialState = {}) {
  let state = { ...initialState };
  const listeners = new Set();

  /** @returns {T} lo stato corrente (copia superficiale: non è modificabile dall'esterno) */
  function getState() {
    return { ...state };
  }

  /**
   * Aggiorna lo stato e notifica gli iscritti.
   * Accetta un oggetto parziale o una funzione `(state) => patch`.
   */
  function setState(patch) {
    const prev = state;
    const partial = typeof patch === 'function' ? patch(getState()) : patch;
    state = { ...state, ...partial };

    for (const listener of listeners) {
      // Un listener che esplode non deve impedire agli altri di aggiornarsi.
      try {
        listener(getState(), prev);
      } catch (error) {
        console.error('[store] errore in un listener:', error);
      }
    }

    return getState();
  }

  /**
   * Registra un osservatore.
   * @returns {() => void} funzione di annullamento dell'iscrizione
   */
  function subscribe(listener) {
    if (typeof listener !== 'function') {
      throw new TypeError('subscribe() richiede una funzione');
    }
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  return { getState, setState, subscribe };
}

/** Stato iniziale dell'applicazione. */
export const createInitialState = () => ({
  /** Elenco completo degli ID restituito da newstories. */
  storyIds: [],
  /** News già scaricate e normalizzate, nell'ordine di arrivo. */
  stories: [],
  /** Pagine già caricate (1 pagina = PAGE_SIZE news). */
  loadedPages: 0,
  /** Caricamento iniziale in corso. */
  isLoading: false,
  /** Caricamento di una pagina successiva in corso. */
  isLoadingMore: false,
  /** Messaggio di errore da mostrare, oppure `null`. */
  error: null,
  /** Testo di ricerca client-side. */
  search: '',
  /** Criterio di ordinamento: newest | oldest | score | comments. */
  sort: 'newest',
  /** ID delle news salvate come preferite. */
  favorites: [],
  /** Se `true` mostra solo i preferiti. */
  showFavoritesOnly: false,
});
